<template>
  <template v-if="subproduct">
    <Section tone="default" pad-y="lg" data-testid="subproduct-page">
      <Container>
        <SubpageHero
          :heading="heading"
          :lead="lead"
          :primary-cta="primaryCta"
          :secondary-cta="secondaryCta"
          @click-hero-button="clickHeroButton"
        />

        <div
          class="mt-12 flex flex-col gap-12 md:gap-16"
          :aria-label="t('aria.subproductSections')"
        >
          <section
            v-for="section in localizedSections"
            :key="section.id"
            :aria-labelledby="`subproduct-${subproduct.id}-${section.id}`"
            class="flex flex-col gap-6"
          >
            <h2
              :id="`subproduct-${subproduct.id}-${section.id}`"
              class="text-2xl font-bold text-ink-strong md:text-3xl"
            >
              {{ section.title }}
            </h2>

            <Grid v-if="section.kind === 'bullets'" :cols="{ base: 1, md: 2 }" :gap="6">
              <Card
                v-for="(item, idx) in section.items"
                :key="`${section.id}-${idx}`"
                tone="default"
                padding="md"
                class="flex h-full items-start gap-3"
              >
                <span
                  class="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent-strong"
                >
                  <BaseIcon name="check" :size="18" />
                </span>
                <span class="text-base text-ink md:text-lg">{{ item }}</span>
              </Card>
            </Grid>

            <ol v-else-if="section.kind === 'numbered'" class="flex flex-col gap-4">
              <li
                v-for="(item, idx) in section.items"
                :key="`${section.id}-${idx}`"
                class="flex items-start gap-4"
              >
                <span
                  class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-base font-bold text-ink-inverse"
                  aria-hidden="true"
                >
                  {{ idx + 1 }}
                </span>
                <span class="pt-1 text-base text-ink md:text-lg">{{ item }}</span>
              </li>
            </ol>

            <Accordion v-else-if="section.kind === 'faq'">
              <AccordionItem
                v-for="(item, idx) in section.items"
                :id="`${section.id}-${idx}`"
                :key="`${section.id}-${idx}`"
                :title="item.title"
                @open="trackOpenAccordionItem"
              >
                <p class="text-sm text-ink-muted md:text-base">
                  {{ item.answer }}
                </p>
              </AccordionItem>
            </Accordion>

            <p
              v-else-if="section.kind === 'prose' && section.paragraph"
              class="max-w-3xl text-base text-ink-muted md:text-lg"
            >
              {{ section.paragraph }}
            </p>
          </section>
        </div>
      </Container>
    </Section>

    <Section tone="accent" pad-y="lg" data-testid="subproduct-closing">
      <Container>
        <div class="flex flex-col items-center gap-6 text-center">
          <h2 class="max-w-2xl text-3xl font-extrabold text-ink-inverse md:text-5xl">
            {{ closingHeading }}
          </h2>
          <p class="max-w-2xl text-base text-ink-inverse/80 md:text-lg">
            {{ closingLead }}
          </p>
          <div class="flex flex-wrap justify-center gap-3">
            <BaseButton variant="secondary" size="lg" @click.prevent="clickBriefButton">
              {{ primaryCta?.label ?? t('cta.startBrief') }}
              <template #icon-right>
                <BaseIcon name="arrow-right" :size="20" />
              </template>
            </BaseButton>
          </div>
        </div>
      </Container>
    </Section>
  </template>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { RouteLocationRaw } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Section from '@/components/primitives/Section.vue';
import Container from '@/components/primitives/Container.vue';
import Grid from '@/components/primitives/Grid.vue';
import Card from '@/components/primitives/Card.vue';
import SubpageHero from '@/components/domain/SubpageHero.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import Accordion from '@/components/feedback/Accordion.vue';
import AccordionItem from '@/components/feedback/AccordionItem.vue';
import { SUBPRODUCT_BY_ID } from '@/content/subproducts';
import type {
  SubproductCta,
  SubproductCtaTarget,
  SubproductSectionFaq,
} from '@/types/component-types';
import type { HeroCta } from '@/components/domain/HeroBlock.vue';
import { useLocale } from '@/composables/useLocale';
import { useSeo } from '@/composables/useSeo';
import {
  pickLocalized,
  mapLocalizedRecords,
  pickLocalizedFaqItems,
  type FaqItemPair,
} from '@/lib/i18n/localize';
import { Cta } from '@/types/types';
import { yaTrackEvent } from '@/lib/metrics';

const { t } = useI18n();
const { locale } = useLocale();
const route = useRoute();
const router = useRouter();

const subproduct = computed(() => {
  const slug = String(route.params.slug ?? '');
  return SUBPRODUCT_BY_ID[slug] ?? null;
});

const briefRoute = computed<RouteLocationRaw>(() => ({
  name: 'brief',
  params: { lang: locale.value },
}));

const homeContactRoute = computed<RouteLocationRaw>(() => ({
  path: `/${locale.value}/`,
  hash: '#contact',
}));

function resolveCtaTo(target: SubproductCtaTarget): RouteLocationRaw {
  if (target === 'brief') return briefRoute.value;
  return homeContactRoute.value;
}

function buildHeroCta(cta: SubproductCta | undefined): HeroCta | null {
  if (!cta) return null;
  const label = pickLocalized(cta, 'label', locale.value);
  return {
    label,
    params: { to: resolveCtaTo(cta.target) },
    //to: resolveCtaTo(cta.target),
  };
}

const heading = computed(() =>
  subproduct.value ? pickLocalized(subproduct.value, 'heading', locale.value) : '',
);

const lead = computed(() =>
  subproduct.value ? pickLocalized(subproduct.value, 'lead', locale.value) : '',
);

const primaryCta = computed<HeroCta | null>(() =>
  subproduct.value ? buildHeroCta(subproduct.value.primaryCta) : null,
);

const secondaryCta = computed<HeroCta | null>(() =>
  subproduct.value ? buildHeroCta(subproduct.value.secondaryCta) : null,
);

type LocalizedSection =
  | {
      readonly id: string;
      readonly title: string;
      readonly kind: 'bullets' | 'numbered' | 'prose';
      readonly items: readonly string[];
      readonly paragraph: string;
    }
  | {
      readonly id: string;
      readonly title: string;
      readonly kind: 'faq';
      readonly items: readonly FaqItemPair[];
      readonly paragraph: string;
    };

const localizedSections = computed<readonly LocalizedSection[]>(() => {
  if (!subproduct.value) return [];
  return subproduct.value.sections.map((section) => {
    if (section.kind === 'faq') {
      return {
        id: section.id,
        kind: section.kind,
        title: pickLocalized(section, 'title', locale.value),
        paragraph: pickLocalized(section, 'paragraph', locale.value),
        items: pickLocalizedFaqItems(section as SubproductSectionFaq, locale.value),
      };
    }
    const [row] = mapLocalizedRecords(
      [section as unknown as Record<string, unknown>],
      locale.value,
      {
        copy: ['id', 'kind'],
        strings: ['title', 'paragraph'],
        arrays: ['items'],
      },
    );
    return row as LocalizedSection;
  });
});

const closingHeading = computed(() =>
  subproduct.value ? pickLocalized(subproduct.value.closing, 'heading', locale.value) : '',
);

const closingLead = computed(() =>
  subproduct.value ? pickLocalized(subproduct.value.closing, 'lead', locale.value) : '',
);

useSeo({
  title: () => heading.value,
  description: () => lead.value,
});

const clickHeroButton = (cta: Cta) => {
  if (cta.params && cta.params.to) {
    yaTrackEvent('NewSiteOpenBrief');
    router.push(cta.params.to);
  }
};

const clickBriefButton = () => {
  yaTrackEvent('NewSiteOpenBrief');
  void router.push(`/${locale.value}/brief`);
};

const trackOpenAccordionItem = (question: string) => {
  yaTrackEvent('NewSiteClickFaqItem', { kind: route.params['slug'], question: question });
};
</script>
